// Barre de la page Assistant (décision de Louka du 01/10) : trois onglets en haut, comme le « Work »
// de Dust.
//   Travail    : Agents, Compétences (pages de l'assistant dans le chat) ; Projets de Dust masqués
//                (décision de Louka du 10/10 : l'équipe ne s'en servirait pas) ; la liste des
//                conversations dessous ; la boîte de réception si elle a du contenu.
//   Entreprise : Connecteurs, Documents, Sites web, Tâches planifiées (pages de l'app).
//   Activités  : bloc Utilisateurs (Webinaires, Quiz, Comptes-rendus) et, pour un admin, bloc
//                Administration (Campagnes quiz, Parcours de quiz, puis Campagnes webinaires :
//                ordre du 16/10, Parcours de quiz ajoutés le 17/10).
// Le modèle de l'app (navigation_app.ts) est réparti ici ; les entrées non citées par Louka
// (Compétences de l'app) ne sont pas reprises.
// Sous-plan 11 : une autre app (Boosts) peut demander, dans son modèle, le titre de sa section
// métier (titreActivites) et toutes ses entrées d'administration (administrationComplete).
// Pirouette n'envoie ni l'un ni l'autre : son rendu est figé par le test.
import { routeTachesPlanifiees } from "@app/lib/compta/fiche_agent";
import type { EntreeNav, IconeNav } from "@app/lib/compta/navigation_app";
import { GLOBAL_SPACE_NAME } from "@app/types/groups";

export type IdOnglet = "travail" | "entreprise" | "activites";

/**
 * Onglet de départ d'après la page ouverte (lien direct, 15/10) : les espaces de l'entreprise et les
 * tâches planifiées vivent sous « Entreprise », le reste sous « Travail ». Sans cela, une page
 * Entreprise ouverte par un lien affichait « Travail » surligné.
 */
export function ongletDeLaRoute(chemin: string, workspaceId: string): IdOnglet {
  const base = `/w/${workspaceId}/`;
  const route = chemin.split(/[?#]/)[0];
  if (
    route.startsWith(`${base}spaces`) ||
    route.startsWith(routeTachesPlanifiees(workspaceId))
  ) {
    return "entreprise";
  }
  return "travail";
}

/** Pictogramme affiché devant une entrée (comme dans les Spaces de Dust). */
export type Pictogramme =
  | "agents"
  | "competences"
  | "connecteurs"
  | "taches"
  | "documents"
  | "sites"
  | "espace"
  // Activités (06/10) : une icône par page métier, comme Travail.
  | "video"
  | "diplome"
  | "compte-rendu"
  | "annonce"
  | "catalogue"
  | "panier"
  | "tableau"
  | "accuse"
  | "fournisseurs"
  | "clients"
  | "calendrier"
  | "paiement"
  | "statistiques"
  // Electron (08/10) : analyse de plans, comparateur CCTP/DPGF, pointage.
  | "plan"
  | "comparateur"
  | "pointage"
  // Pirouette (17/10) : les parcours de quiz assignés.
  | "parcours"
  | "page";

const PICTOGRAMMES_PAR_ID: Record<string, Pictogramme> = {
  // Pirouette
  webinaires: "video",
  "admin-webinars": "video",
  quiz: "diplome",
  "admin-quiz": "diplome",
  "comptes-rendus": "compte-rendu",
  // Parcours de quiz (17/10) : un itinéraire, distinct de l'icône des campagnes quiz.
  "admin-campaigns": "parcours",
  // Boosts
  catalogue: "catalogue",
  panier: "panier",
  dashboard: "tableau",
  accuses: "accuse",
  fournisseurs: "fournisseurs",
  clients: "clients",
  linkedin: "annonce",
  calendrier: "calendrier",
  // Electron (demande de Louka du 08/10 : des icônes comme Travail et Entreprise)
  plans: "plan",
  comparateur: "comparateur",
  pointage: "pointage",
};

// Repli sur le libellé (pages d'administration propres à une app), puis icône par défaut.
const PICTOGRAMMES_PAR_MOT: [RegExp, Pictogramme][] = [
  [/parcours/i, "parcours"],
  [/webinaire|vid[ée]o/i, "video"],
  [/quiz|formation/i, "diplome"],
  [/compte.?rendu|r[ée]union|note/i, "compte-rendu"],
  [/campagne|linkedin|communication|annonce/i, "annonce"],
  [/catalogue|produit/i, "catalogue"],
  [/panier|commande/i, "panier"],
  [/tableau de bord|statisti|usage|monitoring|suivi/i, "statistiques"],
  [/accus[ée]/i, "accuse"],
  [/fournisseur/i, "fournisseurs"],
  [/client|membre|utilisateur|[ée]quipe|compte/i, "clients"],
  [/calendrier|agenda|planning/i, "calendrier"],
  [/factur|paiement|abonnement|tarif/i, "paiement"],
  [/plan/i, "plan"],
  [/comparat|cctp|dpgf/i, "comparateur"],
  [/pointage|heures/i, "pointage"],
];

/** Icône d'une page d'Activités : celle du modèle, sinon par identifiant, par libellé, ou par défaut. */
export function pictogrammeActivite(e: {
  id: string;
  libelle: string;
  pictogramme?: Pictogramme;
}): Pictogramme {
  if (e.pictogramme) {
    return e.pictogramme;
  }
  const parId = PICTOGRAMMES_PAR_ID[e.id];
  if (parId) {
    return parId;
  }
  // Espaces collaboratifs d'Electron, rangés dans « Données » : l'icône des espaces de l'onglet Entreprise.
  if (e.id.startsWith("espace-")) {
    return "espace";
  }
  const parMot = PICTOGRAMMES_PAR_MOT.find(([motif]) => motif.test(e.libelle));
  return parMot ? parMot[1] : "page";
}

/** Entrée d'onglet : une entrée de l'app (navigation par message) ou une page du chat. */
export type EntreeOnglet = EntreeNav & {
  routeAssistant?: string;
  pictogramme?: Pictogramme;
};

export type SectionOnglet = {
  titre?: string;
  /**
   * Activités en sous-catégories (Boosts, 03/10) : la section vient d'une entrée de
   * `fonctionnalites` qui a ses propres enfants ; son titre est un peu plus grand.
   */
  sousCategorie?: boolean;
  /** Les entrées sont rangées sous une ligne dépliable (un « espace » chez Dust). */
  depliable?: { libelle: string; pictogramme: Pictogramme };
  entrees: EntreeOnglet[];
};

export type Onglet = {
  id: IdOnglet;
  libelle: string;
  icone: IconeNav;
  sections: SectionOnglet[];
  /** La liste des conversations s'affiche sous cet onglet. */
  avecConversations: boolean;
  /** RAG Dust : l'arbre des espaces de Dust s'affiche sous les entrees (EspacesDeDust). */
  avecEspaces: boolean;
};

function enfant(
  entrees: EntreeNav[],
  parent: string,
  id: string
): EntreeNav | undefined {
  return entrees
    .find((e) => e.id === parent)
    ?.enfants?.find((c) => c.id === id);
}

function presents<T>(liste: (T | undefined)[]): T[] {
  return liste.filter((x): x is T => x !== undefined);
}

export function construireOnglets(
  entrees: EntreeNav[],
  workspaceId: string,
  options: {
    espacesDust?: boolean;
    agentPrincipal?: string | null;
    /**
     * Sous-plan 11 (une instance, plusieurs apps) : la section Administration reprend tous les
     * enfants de l'entrée `administration`. Sans l'option (Pirouette), seulement les campagnes
     * quiz, les parcours de quiz et les campagnes webinaires.
     */
    administrationComplete?: boolean;
    /** Sous-plan 11 : titre de la section métier d'Activités ; « Utilisateurs » par défaut. */
    titreActivites?: string;
    /**
     * Boite de reception (02/10) : la boite s'ouvre en petite fenetre (BoiteDeReception), ses
     * notifications ne sont plus des entrees de l'onglet Travail.
     */
    boiteSurgissante?: boolean;
  } = {}
): Onglet[] {
  const travail: SectionOnglet[] = [
    {
      entrees: [
        {
          id: "agents",
          libelle: "Agents",
          pictogramme: "agents",
          routeAssistant: `/w/${workspaceId}/builder/agents`,
        },
        {
          id: "competences",
          libelle: "Compétences",
          pictogramme: "competences",
          routeAssistant: `/w/${workspaceId}/builder/skills`,
        },
      ],
    },
  ];
  const boite = entrees.find((e) => e.id === "boite");
  if (!options.boiteSurgissante && boite?.enfants && boite.enfants.length > 0) {
    travail.push({
      titre: boite.badge ? `${boite.libelle} (${boite.badge})` : boite.libelle,
      entrees: boite.enfants,
    });
  }

  const avec = (
    e: EntreeNav | undefined,
    pictogramme: Pictogramme
  ): EntreeOnglet | undefined => (e ? { ...e, pictogramme } : undefined);
  // Comme l'onglet « Spaces » de Dust : les outils en tête, puis l'espace ouvert de l'entreprise.
  // RAG Dust (01/10) : Dust est la seule source des documents. Les dossiers vivent dans l'arbre des
  // espaces de Dust (EspacesDeDust), plus dans la page Documents de l'app ; le site web aussi
  // (connecteur de Dust, migration du 01/10).
  const entreprise: SectionOnglet[] = options.espacesDust
    ? [
        {
          entrees: presents([
            avec(enfant(entrees, "outils", "connectors"), "connecteurs"),
            // Taches planifiees (06/10) : la page des taches dans le chat ; les nouvelles taches
            // s'y creent sur l'assistant principal, d'ou la condition. Sans assistant principal,
            // l'ancienne page de l'app.
            options.agentPrincipal
              ? {
                  id: "taches",
                  libelle: "Tâches planifiées",
                  pictogramme: "taches" as const,
                  routeAssistant: routeTachesPlanifiees(workspaceId),
                }
              : avec(
                  entrees.find((e) => e.id === "taches"),
                  "taches"
                ),
          ]),
        },
      ].filter((s) => s.entrees.length > 0)
    : [
        {
          entrees: presents([
            avec(enfant(entrees, "outils", "connectors"), "connecteurs"),
            avec(
              entrees.find((e) => e.id === "taches"),
              "taches"
            ),
          ]),
        },
        {
          titre: "Espaces ouverts",
          depliable: {
            // Meme nom que l'espace commun de Dust, « Données Entreprise » (decision du 08/10).
            libelle: GLOBAL_SPACE_NAME,
            pictogramme: "espace" as const,
          },
          entrees: presents([
            avec(enfant(entrees, "donnees", "folders"), "documents"),
            avec(enfant(entrees, "donnees", "websites"), "sites"),
          ]),
        },
      ].filter((s) => s.entrees.length > 0);

  const titreActivites =
    typeof options.titreActivites === "string" &&
    options.titreActivites.trim() !== ""
      ? options.titreActivites.trim()
      : "Utilisateurs";
  // Activités en sous-catégories (Boosts, 03/10) : si les entrées métier sont des groupes (une
  // entrée avec enfants), chaque groupe devient une section titrée ; sinon une seule section.
  const metier = entrees.find((e) => e.id === "fonctionnalites")?.enfants ?? [];
  const sectionsMetier: SectionOnglet[] = metier.some(
    (e) => e.enfants !== undefined
  )
    ? metier.map((groupe) => ({
        titre: groupe.libelle,
        sousCategorie: true,
        entrees: groupe.enfants ?? [],
      }))
    : [{ titre: titreActivites, entrees: metier }];
  const activites: SectionOnglet[] = [
    ...sectionsMetier,
    {
      titre: "Administration",
      entrees:
        options.administrationComplete === true
          ? (entrees.find((e) => e.id === "administration")?.enfants ?? [])
          : // Les quiz avant les webinaires (decision de Louka du 16/10), les parcours de quiz
            // juste apres les campagnes quiz (17/10).
            presents([
              enfant(entrees, "administration", "admin-quiz"),
              enfant(entrees, "administration", "admin-campaigns"),
              enfant(entrees, "administration", "admin-webinars"),
            ]),
    },
  ]
    .filter((s) => s.entrees.length > 0)
    .map((s) => ({
      ...s,
      entrees: s.entrees.map((e) => ({
        ...e,
        pictogramme: pictogrammeActivite(e),
      })),
    }));

  return [
    {
      id: "travail",
      libelle: "Travail",
      icone: "fonctionnalites",
      sections: travail,
      avecConversations: true,
      avecEspaces: false,
    },
    {
      id: "entreprise",
      libelle: "Entreprise",
      icone: "donnees",
      sections: entreprise,
      avecConversations: false,
      avecEspaces: options.espacesDust === true,
    },
    {
      id: "activites",
      libelle: "Activités",
      icone: "administration",
      sections: activites,
      avecConversations: false,
      avecEspaces: false,
    },
  ];
}
